/**
 * Recruiter · what a stored recording actually COVERS, and how to say it.
 *
 * A recording is uploaded in parts (the candidate's browser rotates its recorder so a closed tab
 * can't take the whole session with it), and a candidate who resumes after a break adds more. The
 * report used to render the newest part alone under the caption "Full camera + mic recording of
 * the session" — so a session that spanned a browser restart was presented as complete when it was
 * its last 34 seconds. A recording that silently keeps only the tail is worse than no recording,
 * because the report implies coverage the reviewer then makes a hiring decision on.
 *
 * The rule here: state the length we can prove, compare it to the session only when both numbers
 * are real, and say "unknown" rather than infer. A MediaRecorder WebM carries no duration in its
 * header, so "unknown" is a genuine outcome — every part registered before we started measuring
 * has one.
 */

export interface RecordingPart {
  url?: string;
  durationSeconds?: number;
  sha256?: string;
  expiresAt?: string;
}

export type CoverageVerdict = "complete" | "partial" | "unknown";

export interface RecordingCoverage {
  /** Parts that can actually be played, in order. */
  playable: RecordingPart[];
  partCount: number;
  coveredSeconds: number | null;
  sessionSeconds: number | null;
  verdict: CoverageVerdict;
  /** One sentence a reviewer can act on. Never claims more than `verdict` supports. */
  note: string;
}

/** "7:41", "1:02:15". Negative/absent reads as "0:00" rather than something alarming. */
export function formatClock(seconds: number | null | undefined): string {
  const s = Math.max(0, Math.round(seconds ?? 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  return `${h > 0 ? `${h}:` : ""}${mm}:${String(sec).padStart(2, "0")}`;
}

/** Seconds between two ISO instants, or null when either is missing or the pair is nonsense. */
export function spanSeconds(fromIso?: string | null, toIso?: string | null): number | null {
  if (!fromIso || !toIso) return null;
  const ms = new Date(toIso).getTime() - new Date(fromIso).getTime();
  return Number.isFinite(ms) && ms > 0 ? Math.round(ms / 1000) : null;
}

/**
 * Recording is bounded INSIDE the session — it starts once the questions are live and the camera
 * has come up, and stops at wrap-up — so a few seconds' shortfall is normal, not a gap. The
 * tolerance is the larger of 30s and a tenth of the session; anything past that is a real hole a
 * reviewer needs to know about before they weigh what they watched.
 */
export function coverageTolerance(sessionSeconds: number): number {
  return Math.max(30, Math.round(sessionSeconds * 0.1));
}

export function recordingCoverage(
  parts: readonly RecordingPart[],
  coveredSeconds: number | null,
  sessionSeconds: number | null,
): RecordingCoverage {
  const playable = parts.filter((p) => typeof p.url === "string" && p.url.length > 0);
  const partCount = playable.length;
  const partsPhrase = partCount === 1 ? "one part" : `${partCount} parts`;

  if (coveredSeconds === null) {
    return {
      playable,
      partCount,
      coveredSeconds,
      sessionSeconds,
      verdict: "unknown",
      note: `Recorded in ${partsPhrase}. How long it runs isn't stored for this recording, so we can't say how much of the session it covers — watch it through before relying on it.`,
    };
  }

  const length = formatClock(coveredSeconds);
  if (sessionSeconds === null) {
    return {
      playable,
      partCount,
      coveredSeconds,
      sessionSeconds,
      verdict: "unknown",
      note: `${length} of video, recorded in ${partsPhrase}. The session's own start and end times aren't recorded, so we can't compare the two.`,
    };
  }

  const missing = sessionSeconds - coveredSeconds;
  if (missing <= coverageTolerance(sessionSeconds)) {
    return {
      playable,
      partCount,
      coveredSeconds,
      sessionSeconds,
      verdict: "complete",
      note: `${length} of video, recorded in ${partsPhrase} — the session itself ran ${formatClock(sessionSeconds)}, so this covers it.`,
    };
  }
  return {
    playable,
    partCount,
    coveredSeconds,
    sessionSeconds,
    verdict: "partial",
    note: `${length} of video, recorded in ${partsPhrase} — but the session ran ${formatClock(sessionSeconds)}. About ${formatClock(missing)} was never captured: the camera was off, or the candidate's browser closed, for that time. Judge only what you can see here.`,
  };
}
